import React, { useMemo } from "react"

import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

type PathSegment =
  | { type: "key"; key: string }
  | { type: "arrayIndex"; index: number }
  | { type: "arrayPredicate"; key: string; value: string }

/**
 * Parse path like "response.cards[0].status" or "response.cards[cardFormType=CARD_FORM_TYPE_PHYSICAL].status"
 * into segments so we can support array predicates (which card) not just numeric indices.
 */
function parsePath(path: string): PathSegment[] {
  const segments: PathSegment[] = []
  const parts = path.split(".")
  for (const part of parts) {
    const bracketStart = part.indexOf("[")
    const bracketEnd = part.indexOf("]", bracketStart)
    if (bracketStart >= 0 && bracketEnd > bracketStart) {
      const key = part.slice(0, bracketStart).trim()
      const inside = part.slice(bracketStart + 1, bracketEnd).trim()
      if (key) {
        segments.push({ type: "key", key })
      }
      const eq = inside.indexOf("=")
      if (eq >= 0) {
        const predKey = inside.slice(0, eq).trim()
        let predValue = inside.slice(eq + 1).trim()
        if (predValue.startsWith('"') && predValue.endsWith('"')) {
          predValue = predValue.slice(1, -1)
        }
        segments.push({
          type: "arrayPredicate",
          key: predKey,
          value: predValue,
        })
      } else {
        const num = parseInt(inside, 10)
        if (!Number.isNaN(num)) {
          segments.push({ type: "arrayIndex", index: num })
        }
      }
    } else if (part.trim()) {
      segments.push({ type: "key", key: part.trim() })
    }
  }
  return segments
}

function skipWhitespace(str: string, i: number): number {
  while (i < str.length && /[\s\n\r]/.test(str[i])) {
    i++
  }
  return i
}

function skipJsonValue(str: string, start: number): number {
  let i = skipWhitespace(str, start)
  const c = str[i]
  if (c === '"') {
    i++
    while (i < str.length) {
      if (str[i] === "\\") {
        i += 2
        continue
      }
      if (str[i] === '"') {
        return i + 1
      }
      i++
    }
    return str.length
  }
  if (c === "{") {
    let depth = 1
    i++
    while (i < str.length && depth > 0) {
      const ch = str[i]
      if (ch === '"') {
        i = skipJsonValue(str, i)
        continue
      }
      if (ch === "{") {
        depth++
      } else if (ch === "}") {
        depth--
      }
      i++
    }
    return i
  }
  if (c === "[") {
    let depth = 1
    i++
    while (i < str.length && depth > 0) {
      const ch = str[i]
      if (ch === '"') {
        i = skipJsonValue(str, i)
        continue
      }
      if (ch === "[") {
        depth++
      } else if (ch === "]") {
        depth--
      }
      i++
    }
    return i
  }
  if (c === "-" || (c >= "0" && c <= "9")) {
    while (i < str.length && /[-0-9eE.+ ]/.test(str[i])) {
      i++
    }
    return i
  }
  if (str.slice(i, i + 4) === "true") {
    return i + 4
  }
  if (str.slice(i, i + 5) === "false") {
    return i + 5
  }
  if (str.slice(i, i + 4) === "null") {
    return i + 4
  }
  return i
}

/**
 * Check if the object substring (from scope) has the top-level key with the expected value.
 * Used to find the array element matching an array predicate. Only checks top-level
 * to avoid matching the same key inside nested structures.
 */
function objectContainsKeyValue(
  objStr: string,
  key: string,
  value: string,
): boolean {
  try {
    const obj = JSON.parse(objStr) as Record<string, unknown>
    if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
      return false
    }
    const actual = obj[key]
    if (actual === undefined) {
      return false
    }
    const expectedStr = value
    if (typeof actual === "string") {
      return actual === expectedStr
    }
    if (typeof actual === "number" || typeof actual === "boolean") {
      return String(actual) === expectedStr
    }
    if (actual === null) {
      return expectedStr === "null"
    }
    return false
  } catch {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const keyPattern = new RegExp(
      `"${escapedKey}"\\s*:\\s*"(${value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})"`,
    )
    if (keyPattern.test(objStr)) {
      return true
    }
    const keyPattern2 = new RegExp(
      `"${escapedKey}"\\s*:\\s*(${value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})(?=\\s*[,\\}])`,
    )
    return keyPattern2.test(objStr)
  }
}

/**
 * Find the character range [start, end) of the value at the given path in formatted JSON.
 * Supports array predicates e.g. cards[cardFormType=CARD_FORM_TYPE_PHYSICAL] to pick
 * the correct card (physical vs virtual) instead of the first one.
 */
function findValueRangeAtPath(
  formattedJson: string,
  path: string,
): { start: number; end: number } | null {
  const normalizedPath = path.replace(/^\$\.?/, "").trim()
  const segments = parsePath(normalizedPath)
  if (segments.length === 0) {
    return null
  }

  let rangeStart = skipWhitespace(formattedJson, 0)
  let rangeEnd = skipJsonValue(formattedJson, rangeStart)

  for (const segment of segments) {
    const scope = formattedJson.slice(rangeStart, rangeEnd)
    const scopeStart = rangeStart

    if (segment.type === "arrayIndex") {
      const index = segment.index
      let i = skipWhitespace(scope, 0)
      if (scope[i] !== "[") {
        return null
      }
      i++
      let found = 0
      while (found <= index && i < scope.length) {
        i = skipWhitespace(scope, i)
        if (scope[i] === "]") {
          return null
        }
        const valueStart = scopeStart + i
        const valueEnd = scopeStart + skipJsonValue(scope, i)
        if (found === index) {
          rangeStart = valueStart
          rangeEnd = valueEnd
          break
        }
        i = valueEnd - scopeStart
        i = skipWhitespace(scope, i)
        if (scope[i] === ",") {
          i++
        }
        found++
      }
      if (found !== index) {
        return null
      }
    } else if (segment.type === "arrayPredicate") {
      const { key: predKey, value: predValue } = segment
      let i = skipWhitespace(scope, 0)
      if (scope[i] !== "[") {
        return null
      }
      i++
      let found = false
      while (i < scope.length) {
        i = skipWhitespace(scope, i)
        if (scope[i] === "]") {
          return null
        }
        const valueStart = scopeStart + i
        const valueEnd = scopeStart + skipJsonValue(scope, i)
        const elementStr = formattedJson.slice(valueStart, valueEnd)
        if (objectContainsKeyValue(elementStr, predKey, predValue)) {
          rangeStart = valueStart
          rangeEnd = valueEnd
          found = true
          break
        }
        i = valueEnd - scopeStart
        i = skipWhitespace(scope, i)
        if (scope[i] === ",") {
          i++
        }
      }
      if (!found) {
        return null
      }
    } else {
      const key = segment.key
      let i = skipWhitespace(scope, 0)
      if (scope[i] === "[") {
        const num = parseInt(key, 10)
        if (!Number.isNaN(num) && key === String(num)) {
          const index = num
          i++
          let found = 0
          while (found <= index && i < scope.length) {
            i = skipWhitespace(scope, i)
            if (scope[i] === "]") {
              return null
            }
            const valueStart = scopeStart + i
            const valueEnd = scopeStart + skipJsonValue(scope, i)
            if (found === index) {
              rangeStart = valueStart
              rangeEnd = valueEnd
              break
            }
            i = valueEnd - scopeStart
            i = skipWhitespace(scope, i)
            if (scope[i] === ",") {
              i++
            }
            found++
          }
          if (found !== index) {
            return null
          }
        } else {
          return null
        }
      } else if (scope[i] !== "{") {
        return null
      } else {
        i++
        while (i < scope.length) {
          i = skipWhitespace(scope, i)
          if (scope[i] === "}") {
            return null
          }
          if (scope[i] !== '"') {
            return null
          }
          const keyStart = i
          const keyEnd = scope.indexOf('"', i + 1)
          if (keyEnd === -1) {
            return null
          }
          const currentKey = scope.slice(keyStart + 1, keyEnd)
          i = skipWhitespace(scope, keyEnd + 1)
          if (scope[i] !== ":") {
            return null
          }
          i = skipWhitespace(scope, i + 1)
          const valueStart = scopeStart + i
          const valueEnd = scopeStart + skipJsonValue(scope, i)
          if (currentKey === key) {
            rangeStart = valueStart
            rangeEnd = valueEnd
            break
          }
          i = valueEnd - scopeStart
          i = skipWhitespace(scope, i)
          if (scope[i] === ",") {
            i++
          }
        }
      }
    }
  }

  return { start: rangeStart, end: rangeEnd }
}

/**
 * Fallback: find a range for extractedValue in formatted JSON when path-based lookup fails.
 * Prefer last occurrence so in arrays we're more likely to get the "current" item.
 */
function findValueRangeFallback(
  formattedJson: string,
  extractedValue: unknown,
): { start: number; end: number } | null {
  const valueStr = JSON.stringify(extractedValue)
  let index = formattedJson.indexOf(valueStr)
  if (
    index === -1 &&
    typeof extractedValue === "object" &&
    extractedValue !== null
  ) {
    const pretty = JSON.stringify(extractedValue, null, 2)
    index = formattedJson.indexOf(pretty)
    if (index !== -1) {
      return { start: index, end: index + pretty.length }
    }
  }
  if (index === -1) {
    return null
  }
  let lastIndex = index
  while (true) {
    const next = formattedJson.indexOf(valueStr, lastIndex + 1)
    if (next === -1) {
      break
    }
    lastIndex = next
  }
  return { start: lastIndex, end: lastIndex + valueStr.length }
}

/**
 * Highlights the value at match_path in the formatted JSON.
 * Uses path-based location including array predicates (e.g. cardFormType=CARD_FORM_TYPE_PHYSICAL)
 * so the correct array element is highlighted (e.g. physical checking card, not virtual).
 */
export const HighlightedJson: React.FC<{
  json: unknown
  matchPath?: string
  extractedValue?: unknown
}> = ({ json, matchPath, extractedValue }) => {
  const highlightedJson = useMemo(() => {
    const normalized =
      typeof json === "string"
        ? (() => {
            try {
              return JSON.parse(json) as unknown
            } catch {
              return json
            }
          })()
        : json

    const formattedJson = JSON.stringify(normalized, null, 2) ?? "null"

    if (!matchPath || extractedValue === undefined) {
      return formattedJson
    }

    let range = findValueRangeAtPath(formattedJson, matchPath)
    if (!range) {
      range = findValueRangeFallback(formattedJson, extractedValue)
    }
    if (range) {
      return [
        { text: formattedJson.slice(0, range.start), highlight: false },
        { text: formattedJson.slice(range.start, range.end), highlight: true },
        { text: formattedJson.slice(range.end), highlight: false },
      ]
    }

    return formattedJson
  }, [json, matchPath, extractedValue])

  if (typeof highlightedJson === "string") {
    return <span className="whitespace-pre">{highlightedJson}</span>
  }

  const parts =
    Array.isArray(highlightedJson) && highlightedJson.length > 0
      ? highlightedJson
      : [
          {
            text: stringifyUnknownValue(highlightedJson ?? ""),
            highlight: false,
          },
        ]

  return (
    <span className="whitespace-pre">
      {parts.map((part, index) =>
        part.highlight ? (
          <mark
            key={index}
            id="highlighted-match"
            className="bg-yellow-200 dark:bg-yellow-700/50 rounded px-0.5"
          >
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </span>
  )
}
